/**
 * Dashboard → Platform → "Platform at a Glance" detail panels (Gordon
 * 2026-11-01: "a little more in-depth behind each glance block… slides open
 * more in-depth data below"). Pure summaries — no Firebase imports — so the
 * page (works/releases it already loaded) and the server route
 * (`/api/dashboard/glance`, users/artists/marketplace/events) share them, and
 * they run under `node --test`. Every result is an aggregate or a short list of
 * PUBLIC items (newest releases, items, events); never a person's data.
 */

import { catalogueDate } from './catalogue-analytics';
import { realPhoto } from './placeholder-photo';

export type GlanceKey = 'users' | 'tracks' | 'releases' | 'artists' | 'marketplace' | 'events';
export const GLANCE_KEYS: GlanceKey[] = ['users', 'tracks', 'releases', 'artists', 'marketplace', 'events'];
/** Sections the page can't compute from what it already loads — fetched on open. */
export const SERVER_GLANCE_SECTIONS = ['users', 'artists', 'marketplace', 'events'] as const;
export type ServerGlanceSection = typeof SERVER_GLANCE_SECTIONS[number];

export interface Count<K extends string = string> { key: K; value: number }
/** `key` is YYYY-MM (UTC); the panel formats the label in the reader's language. */
export interface MonthCount { key: string; count: number }

type Doc = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const sortCounts = <K extends string>(map: Map<K, number>): Count<K>[] =>
  [...map.entries()].filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([key, value]) => ({ key, value }));
const bump = <K>(map: Map<K, number>, key: K, by = 1) => map.set(key, (map.get(key) ?? 0) + by);
const iso = (d: Date | null): string | null => (d ? d.toISOString() : null);
const DAY = 86_400_000;

/** The last `n` calendar months (UTC), oldest first. */
export function lastMonths(now: Date, n: number): MonthCount[] {
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (n - 1) + i, 1));
    return { key: d.toISOString().slice(0, 7), count: 0 };
  });
}

/** Same rule as services/artists slugify — the artist page resolves it back. */
export function artistSlug(name: string): string {
  return (name || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// ── Users ────────────────────────────────────────────────────────────────────

export type AccountKind = 'fan' | 'business' | 'creator' | 'venue' | 'admin' | 'unset';

/** Account types as stored over the years → the five the app knows today. */
export function accountKind(accountType: unknown, placeholder?: unknown): AccountKind | 'placeholder' {
  const t = str(accountType).toLowerCase();
  if (placeholder === true || t === 'ghost' || t === 'placeholder') return 'placeholder';
  if (t === 'consumer' || t === 'fan' || t === 'listener' || t === 'consumer-private') return 'fan';
  if (t === 'consumer-business' || t === 'business') return 'business';
  if (t === 'creator' || t === 'artist' || t === 'producer') return 'creator';
  if (t === 'venue') return 'venue';
  if (t === 'admin' || t === 'superadmin') return 'admin';
  return 'unset';
}

export interface UsersGlance {
  /** User profile docs (placeholders excluded). */
  profiles: number;
  /** Claimable placeholder accounts made for credited people not on BB yet. */
  placeholders: number;
  byKind: Count<AccountKind>[];
  /** New accounts per month, last 12 months. */
  signups: MonthCount[];
  /** Where sign-up dates came from: the sign-in system, or profile createdAt. */
  signupsSource: 'auth' | 'profiles';
  new30: number;
  /** Accounts that signed in during the last 30 / 7 days — null when unknown. */
  active30: number | null;
  active7: number | null;
}

export interface AuthMeta { created?: string | null; lastSeen?: string | null }

export function summariseUsers(profiles: Doc[], auth: AuthMeta[] | null, now = new Date()): UsersGlance {
  const kinds = new Map<AccountKind, number>();
  let placeholders = 0;
  const profileDates: Date[] = [];
  for (const p of profiles) {
    const kind = accountKind(p.accountType, p.placeholder);
    if (kind === 'placeholder') { placeholders++; continue; }
    bump(kinds, kind);
    const d = catalogueDate(p.createdAt);
    if (d) profileDates.push(d);
  }
  const fromAuth = !!auth && auth.length > 0;
  const created = fromAuth
    ? auth!.map(a => catalogueDate(a.created ?? null)).filter((d): d is Date => !!d)
    : profileDates;
  const signups = lastMonths(now, 12);
  let new30 = 0;
  for (const d of created) {
    if (d > now) continue;
    if (now.getTime() - d.getTime() <= 30 * DAY) new30++;
    const bucket = signups.find(m => m.key === d.toISOString().slice(0, 7));
    if (bucket) bucket.count++;
  }
  let active30: number | null = null, active7: number | null = null;
  if (fromAuth) {
    active30 = 0; active7 = 0;
    for (const a of auth!) {
      const d = catalogueDate(a.lastSeen ?? null);
      if (!d) continue;
      const age = now.getTime() - d.getTime();
      if (age <= 30 * DAY) active30++;
      if (age <= 7 * DAY) active7++;
    }
  }
  return {
    profiles: profiles.length - placeholders,
    placeholders,
    byKind: sortCounts(kinds),
    signups,
    signupsSource: fromAuth ? 'auth' : 'profiles',
    new30,
    active30,
    active7,
  };
}

// ── Tracks ───────────────────────────────────────────────────────────────────

export interface TopArtist { id: string; name: string; href: string; tracks: number }
export interface TracksGlance {
  explicit: number;
  /** Tracks with a tempo or key worked out (Analyse BPM & key). */
  analysed: number;
  /** Most tracks first — by artistId, else by artist name. */
  topArtists: TopArtist[];
}

function workArtist(w: Doc): { id: string; name: string } {
  const a = w.artist as unknown;
  const name = str(w.artistName) || (typeof a === 'string' ? a.trim() : str((a as Doc | undefined)?.name));
  const id = str(w.artistId) || str((a as Doc | undefined)?.docId) || str((a as Doc | undefined)?.id);
  return { id, name };
}

/** `works` = the audio works the page already loaded for Catalogue Intelligence. */
export function summariseTracks(works: Doc[], topN = 8): TracksGlance {
  let explicit = 0, analysed = 0;
  const artists = new Map<string, TopArtist>();
  for (const w of works) {
    if (w.explicit === true) explicit++;
    if (Number(w.bpm) > 0 || str(w.musicalKey) || str(w.key)) analysed++;
    const { id, name } = workArtist(w);
    if (!id && !name) continue;
    const k = id ? `id:${id}` : `name:${name.toLowerCase()}`;
    const cur = artists.get(k);
    if (cur) { cur.tracks++; if (!cur.name && name) cur.name = name; continue; }
    const slug = artistSlug(name);
    artists.set(k, { id: id || slug, name, href: id ? `/artists/${id}` : `/artists/${slug}`, tracks: 1 });
  }
  const topArtists = [...artists.values()]
    .filter(a => a.name && a.href !== '/artists/')
    .sort((a, b) => b.tracks - a.tracks || a.name.localeCompare(b.name))
    .slice(0, topN);
  return { explicit, analysed, topArtists };
}

// ── Releases ─────────────────────────────────────────────────────────────────

export type ReleaseKind = 'album' | 'ep' | 'single' | 'mixtape' | 'compilation' | 'live' | 'other' | 'unset';

export function releaseKind(v: unknown): ReleaseKind {
  const t = str(v).toLowerCase();
  if (!t) return 'unset';
  if (t === 'album' || t === 'lp') return 'album';
  if (t === 'ep') return 'ep';
  if (t === 'single') return 'single';
  if (t === 'mixtape') return 'mixtape';
  if (t === 'compilation') return 'compilation';
  if (t.startsWith('live')) return 'live';
  return 'other';
}

export interface GlanceListItem {
  id: string;
  title: string;
  /** Artist, category or place — shown under the title. */
  sub: string;
  imageUrl: string;
  /** ISO date, when known. */
  date: string | null;
  href: string;
}

export interface ReleasesGlance {
  total: number;
  byKind: Count<ReleaseKind>[];
  avgTracks: number;
  withCover: number;
  /** Releases with at least one track that has audio. */
  playable: number;
  newest: GlanceListItem[];
}

const releaseTracks = (r: Doc): Doc[] => (Array.isArray(r.tracks) ? (r.tracks as Doc[]) : []);
const releaseCover = (r: Doc): string =>
  str(r.coverArtUrl) || str(r.imageUrl) || (Array.isArray(r.images) ? str(r.images[0]) : '');
const releaseArtist = (r: Doc): string => {
  const a = r.artist as unknown;
  return str(r.artistName) || (typeof a === 'string' ? a.trim() : str((a as Doc | undefined)?.name));
};

/** Same visibility rule as the dashboard's Recent Releases (and home). */
export function releaseIsVisible(r: Doc, hiddenWorkIds: Set<string>): boolean {
  if (r.adminHidden || r.hidden === true) return false;
  const ids = releaseTracks(r).map(t => str(t.workId) || str(t.id)).filter(Boolean);
  return !(ids.length > 0 && ids.every(id => hiddenWorkIds.has(id)));
}

export function summariseReleases(releases: Doc[], hiddenWorkIds: Set<string> = new Set(), newestN = 5): ReleasesGlance {
  const kinds = new Map<ReleaseKind, number>();
  let tracks = 0, withCover = 0, playable = 0;
  const listable: { r: Doc; date: Date | null }[] = [];
  for (const r of releases) {
    bump(kinds, releaseKind(r.releaseType ?? r.type));
    const ts = releaseTracks(r);
    tracks += ts.length;
    if (releaseCover(r)) withCover++;
    const audible = ts.some(t => (str(t.mp3Url) || str(t.audioUrl)) && !hiddenWorkIds.has(str(t.workId) || str(t.id)));
    if (audible) playable++;
    if (audible && releaseIsVisible(r, hiddenWorkIds)) listable.push({ r, date: catalogueDate(r.releaseDate) ?? catalogueDate(r.createdAt) });
  }
  listable.sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0));
  return {
    total: releases.length,
    byKind: sortCounts(kinds),
    avgTracks: releases.length ? tracks / releases.length : 0,
    withCover,
    playable,
    newest: listable.slice(0, newestN).map(({ r, date }) => ({
      id: str(r.id),
      title: str(r.title),
      sub: releaseArtist(r),
      imageUrl: releaseCover(r),
      date: iso(date),
      href: `/music/${str(r.id)}`,
    })),
  };
}

// ── Artists ──────────────────────────────────────────────────────────────────

export interface ArtistsGlance {
  total: number;
  /** Linked to at least one BB account (owner, connected or legacy link). */
  claimed: number;
  /** Nobody has claimed it yet. */
  unclaimed: number;
  withPhoto: number;
  withoutPhoto: number;
  hidden: number;
}

export function summariseArtists(artists: Doc[]): ArtistsGlance {
  let claimed = 0, withPhoto = 0, hidden = 0;
  for (const a of artists) {
    const connected = Array.isArray(a.connectedUserIds) && a.connectedUserIds.some(x => str(x));
    if (str(a.ownerUid) || connected || str(a.linkedUserId)) claimed++;
    if (realPhoto(str(a.imageUrl))) withPhoto++;
    if (a.adminHidden === true || a.hidden === true) hidden++;
  }
  return {
    total: artists.length,
    claimed,
    unclaimed: artists.length - claimed,
    withPhoto,
    withoutPhoto: artists.length - withPhoto,
    hidden,
  };
}

// ── Marketplace ──────────────────────────────────────────────────────────────

export type PricingKind = 'fixed' | 'pwyw' | 'donation' | 'free';

export function pricingKind(item: Doc): PricingKind {
  const m = str(item.pricingModel).toLowerCase();
  if (m === 'pay_what_you_want' || m === 'pwyw') return 'pwyw';
  if (m === 'donation_only' || m === 'donation') return 'donation';
  if (m === 'free') return 'free';
  return Number(item.price) > 0 ? 'fixed' : 'free';
}

export interface MarketplaceGlance {
  total: number;
  /** Active and not hidden — what shoppers see. */
  listed: number;
  /** Drafts, sold out, paused, archived or hidden. */
  notListed: number;
  byCategory: Count[];
  byPricing: Count<PricingKind>[];
  /** Fixed prices in the most common currency, in major units (as stored). */
  price: { currency: string; min: number; median: number; max: number; n: number } | null;
  newest: GlanceListItem[];
}

function itemImage(item: Doc): string {
  const media = Array.isArray(item.media) ? (item.media as unknown[]) : [];
  const m = media.find(x => typeof x === 'string' || !(x as Doc)?.type || (x as Doc).type === 'image');
  return (typeof m === 'string' ? m : str((m as Doc | undefined)?.url)) || str(item.imageUrl) || str(item.scrapedImageUrl);
}

export function summariseMarketplace(items: Doc[], newestN = 5): MarketplaceGlance {
  const listedItems = items.filter(i => str(i.status || 'active').toLowerCase() === 'active' && i.adminHidden !== true);
  const cats = new Map<string, number>();
  const pricing = new Map<PricingKind, number>();
  const prices = new Map<string, number[]>();
  for (const i of listedItems) {
    bump(cats, str(i.categoryKey).toLowerCase() || 'other');
    const kind = pricingKind(i);
    bump(pricing, kind);
    const price = Number(i.price);
    if (kind === 'fixed' && price > 0) {
      const cur = (str(i.currency) || 'USD').toUpperCase();
      prices.set(cur, [...(prices.get(cur) ?? []), price]);
    }
  }
  const main = [...prices.entries()].sort((a, b) => b[1].length - a[1].length)[0];
  let price: MarketplaceGlance['price'] = null;
  if (main) {
    const sorted = [...main[1]].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    price = {
      currency: main[0],
      min: sorted[0],
      max: sorted[sorted.length - 1],
      median: sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2,
      n: sorted.length,
    };
  }
  const newest = listedItems
    .map(i => ({ i, date: catalogueDate(i.createdAt) ?? catalogueDate(i.updatedAt) }))
    .sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0))
    .slice(0, newestN)
    .map(({ i, date }) => ({
      id: str(i.id),
      title: str(i.title),
      sub: str(i.categoryKey).replace(/-/g, ' '),
      imageUrl: itemImage(i),
      date: iso(date),
      href: `/marketplace/${str(i.id)}`,
    }));
  return {
    total: items.length,
    listed: listedItems.length,
    notListed: items.length - listedItems.length,
    byCategory: sortCounts(cats).slice(0, 8),
    byPricing: sortCounts(pricing),
    price,
    newest,
  };
}

// ── Events ───────────────────────────────────────────────────────────────────

export interface EventsGlance {
  total: number;
  upcoming: number;
  past: number;
  undated: number;
  /** Posted by people on BB (what /events lists). */
  bbMade: number;
  /** Listings brought in by importers/scrapers. */
  imported: number;
  byCategory: Count[];
  /** Next upcoming events as /events shows them. */
  next: GlanceListItem[];
}

const eventDate = (e: Doc): Date | null =>
  catalogueDate(e.startTime) ?? catalogueDate(e.date) ?? catalogueDate(e.startDate);

/** Same rule as services/events getEvents: importers and system posts aren't BB-made. */
export function eventIsImported(e: Doc): boolean {
  const source = str(e.source).toLowerCase();
  if (source === 'aa-scraper' || source.startsWith('scraper:') || source.startsWith('industry-rss:')) return true;
  return e.postedBy === 'system' || e.createdBy === 'system';
}

function eventListed(e: Doc): boolean {
  if (eventIsImported(e) || e.adminHidden === true) return false;
  const status = (str(e.status) || 'published').toLowerCase();
  return !['draft', 'cancelled', 'archived', 'private'].includes(status);
}

function eventPlace(e: Doc): string {
  const loc = (e.location ?? {}) as Doc;
  return [str(e.venueName), str(loc.city)].filter(Boolean).join(' · ') || (typeof e.location === 'string' ? e.location.trim() : '');
}

export function summariseEvents(events: Doc[], now = new Date(), nextN = 5): EventsGlance {
  let upcoming = 0, past = 0, undated = 0, imported = 0;
  const cats = new Map<string, number>();
  const nextUp: { e: Doc; date: Date }[] = [];
  // An event that started today still counts as upcoming until the day is over.
  const cutoff = now.getTime() - 12 * 3_600_000;
  for (const e of events) {
    if (eventIsImported(e)) imported++;
    const cat = str(e.category).toLowerCase();
    if (cat) bump(cats, cat);
    const d = eventDate(e);
    if (!d) { undated++; continue; }
    if (d.getTime() >= cutoff) {
      upcoming++;
      if (eventListed(e)) nextUp.push({ e, date: d });
    } else past++;
  }
  nextUp.sort((a, b) => a.date.getTime() - b.date.getTime());
  return {
    total: events.length,
    upcoming,
    past,
    undated,
    bbMade: events.length - imported,
    imported,
    byCategory: sortCounts(cats).slice(0, 6),
    next: nextUp.slice(0, nextN).map(({ e, date }) => ({
      id: str(e.id),
      title: str(e.title),
      sub: eventPlace(e),
      imageUrl: str(e.imageUrl),
      date: iso(date),
      href: `/events/${str(e.id)}`,
    })),
  };
}

export type ServerGlance = {
  users: UsersGlance;
  artists: ArtistsGlance;
  marketplace: MarketplaceGlance;
  events: EventsGlance;
};
